import { supportsPopups } from '@krakenjs/belter'
import { CheckoutManagedUserAgent } from '../model'

declare global {
  interface Window {
    iabVersion?: string
  }
}

let cookiesSupported

export const supportsCookies = (): boolean => {
  if (cookiesSupported === undefined) {
    // From Modernizr https://github.com/Modernizr/Modernizr/blob/master/feature-detects/cookies.js
    try {
      document.cookie = 'cookietest=1'
      const ret = document.cookie.includes('cookietest=')
      document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT'
      cookiesSupported = ret
    } catch (e) {
      cookiesSupported = false
    }
  }
  return cookiesSupported
}

const uaMatch = (reg: RegExp): boolean => {
  return reg.test(navigator.userAgent)
}

const vendorMatch = (reg: RegExp): boolean => {
  return reg.test(navigator.vendor)
}

export const isMobile = (): boolean => {
  return uaMatch(/Android/i) || uaMatch(/BlackBerry/i) || uaMatch(/iPhone|iPad|iPod/i) || uaMatch(/Opera Mini/i) || uaMatch(/IEMobile/i)
}

export const isMobileChrome = (): boolean => {
  return vendorMatch(/Google/i) && isMobile()
}

export const isMobileSafari = (): boolean => {
  return isSafari() && isMobile()
}

export const isSafari = (): boolean => {
  return vendorMatch(/Apple/i)
}

export const isMobileSamsung = (): boolean => {
  return uaMatch(/SamsungBrowser/i) && isMobile()
}

export const isInAppBrowser = (): boolean => {
  return !supportsPopups() || isCustomInAppBrowser()
}

export const inAppBrowserVersion = (): string => {
  try {
    return window.iabVersion ?? window.xprops.iabVersion ?? '1.0'
  } catch {
    return '1.0'
  }
}

export const isAfterpayInAppBrowser = (): boolean => {
  const afterpayMobile = CheckoutManagedUserAgent.AfterpayMobile
  return uaMatch(new RegExp(afterpayMobile))
}

export const isCashAppInAppBrowser = (): boolean => {
  const cashAppMobile = CheckoutManagedUserAgent.CashAppMobile
  return uaMatch(new RegExp(cashAppMobile))
}

export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch (e) {
    return false
  }
}

/**
 * The following user agents have been identified from merchants that are using custom in-app implementations:
 *
 * THE%20ICONIC/4.154.0-4/iOS/16.0
 * Sephora 22.21.1, iOS 16.0, iPhone12,1/20.dummy
 * WayfairApp/20221019.7634 5.172.2 (iPhone; iOS 16.0; Scale/3.00)
 * The RealReal/2.75.1 (com.realreal.shop; build:20221027.8495; iOS 16.0.3; Optional("iPhone14,3")) Alamofire/2.75.1
 * AloIOS/2.21.0
 * Ishikawa/5.36.0
 * Puma/1.2.2.839 CFNetwork/1390 Darwin/22.0.0 (iPhone iOS/16.0.1) PUMAMobileApp
 * wviPhone/10.4.0 (iPhone/16.1) com.webjet.client
 * Dental 99 iOS HTTP Client/1.9.24 (iOS/1.9.24 ; Merchant/70669 https://api.dental99.com.au/v3
 */
const isCustomInAppBrowser = (): boolean => {
  return uaMatch(/ICONIC|Sephora|WayfairApp|The RealReal|AloIOS|Ishikawa|PUMAMobileApp|com.webjet.client|Dental 99/i)
}

export const supportsBiometricWithoutUserInteraction = (): boolean => {
  // Object.hasOwn was introduced in Safari 15.4, which is the same version that support for biometric prompts
  // without user interaction was added (https://developer.apple.com/documentation/safari-release-notes/safari-15_4-release-notes).
  // In addition, this property exists in all other modern browsers, so is a handy attribute to check.
  return Object.hasOwn !== undefined
}

// ipad ios 13 has the same platform as mac so distinguish by touch points https://stackoverflow.com/questions/57765958/how-to-detect-ipad-and-ipad-os-version-in-ios-13-and-up
export const isIOS = (): boolean => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints !== undefined)
  )
}
export const isAndroid = (): boolean => {
  return uaMatch(/Android/i)
}
export const getIOSVersion = (): number | undefined => {
  if (isIOS()) {
    const userAgent = navigator.userAgent?.match(/OS [\d_]+/i)

    const version =
      userAgent !== null
        ? userAgent[0]
            .substr(3)
            .split('_')
            .map((n) => parseInt(n))
        : undefined
    return version?.[0]
  }
}

export const getSafariVersion = (): number | undefined => {
  if (isSafari()) {
    const index = navigator.userAgent.indexOf('Version')
    return parseFloat(navigator.userAgent.substring(index + 'Version'.length + 1))
  }
}

export const isBelowIOS16AndSafari16 = (): boolean => {
  if (isIOS()) {
    const iosVersion = getIOSVersion()
    if (iosVersion !== undefined && iosVersion < 16) {
      return true
    }
  }
  if (isSafari()) {
    const safariVersion = getSafariVersion()
    if (safariVersion !== undefined && safariVersion < 16) {
      return true
    }
  }

  return false
}

export const isDeepLinkFriendly = (): boolean => {
  const isEligibleOs = isIOS() || isAndroid()
  return isMobile() && isEligibleOs
}
